<template>
  <div class="h-full flex flex-col">
    <v-toolbar density="compact" color="surface" class="border-b shrink-0">
      <v-icon icon="mdi-comment-outline" class="ms-3" />
      <div class="grow text-sm ms-2">{{ t("comments.title") }}</div>
      <v-btn
        v-tooltip="t('comments.close')"
        icon="mdi-close"
        variant="text"
        size="small"
        :aria-label="t('comments.close')"
        @click="emit('close')"
      />
    </v-toolbar>
    <div ref="messageList" class="grow min-h-0 overflow-y-auto p-3 flex flex-col gap-3">
      <div
        v-if="!comments.length"
        class="grow flex items-center justify-center text-sm text-medium-emphasis"
      >
        {{ t("comments.empty") }}
      </div>
      <template v-for="group in groups" :key="group.id">
        <div
          class="self-center px-3 py-1 rounded-full bg-surface-light text-xs text-medium-emphasis"
        >
          {{ group.label }}
        </div>
        <div
          v-for="comment in group.comments"
          :key="comment.id"
          class="flex flex-col gap-1 max-w-[85%]"
          :class="
            commentAuthorTeam(comment.author) === 'aircap'
              ? 'self-end items-end'
              : 'self-start items-start'
          "
        >
          <div
            class="text-xs text-medium-emphasis flex flex-wrap items-center gap-1"
            :class="
              commentAuthorTeam(comment.author) === 'aircap' ? 'justify-end' : 'justify-start'
            "
          >
            <span class="font-medium truncate">{{
              isOwnComment(comment, user.uid)
                ? t("comments.you")
                : commentAuthorLabel(comment.author)
            }}</span>
            <v-chip
              v-if="comment.targetType !== targets[0]?.targetType"
              size="x-small"
              label
              variant="tonal"
            >
              {{
                comment.targetType === "transaction"
                  ? t("comments.fromTransaction")
                  : t("comments.fromTransferRequest")
              }}
            </v-chip>
          </div>
          <div
            class="px-3 py-2 rounded-xl text-sm whitespace-pre-wrap break-words"
            :class="
              commentAuthorTeam(comment.author) === 'aircap'
                ? 'bg-primary rounded-br-sm'
                : 'bg-surface-light rounded-bl-sm'
            "
          >
            {{ comment.text }}
          </div>
          <span class="text-xs text-medium-emphasis">{{
            timeFormatter.format(comment.createdAt.toDate())
          }}</span>
        </div>
      </template>
    </div>
    <v-form
      v-if="user.hasRole(accountCommenterRoles, { account })"
      :disabled="sending"
      class="border-t border-gray-300 p-2 flex items-end gap-2 shrink-0"
      @submit.prevent="addComment"
    >
      <v-textarea
        v-model="text"
        :placeholder="t('comments.placeholder')"
        variant="outlined"
        density="compact"
        rows="2"
        max-rows="6"
        auto-grow
        hide-details
        @keydown.enter.exact.prevent="addComment"
      />
      <v-btn
        v-tooltip="t('comments.send')"
        type="submit"
        icon="mdi-send"
        color="primary"
        variant="flat"
        size="small"
        :aria-label="t('comments.send')"
        :loading="sending"
        :disabled="sending || !text.trim()"
      />
    </v-form>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { call } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import { isOwnComment } from "@/utils/comments";
import type { Account } from "@aircap/common/account";
import { accountCommenterRoles } from "@aircap/common/auth";
import {
  commentAuthorLabel,
  commentAuthorTeam,
  type Comment,
  type CommentTarget,
} from "@aircap/common/comment";
import { captureException } from "@sentry/vue";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import type { VueFirestoreQueryData } from "vuefire";

const props = defineProps<{
  accountId: string;
  account: (Account & { readonly id: string }) | null | undefined;
  /** New messages go to the first target; the others belong to the same thread. */
  targets: CommentTarget[];
  comments: VueFirestoreQueryData<Comment>;
  unreadCount: number;
}>();

const emit = defineEmits<{ close: [] }>();

const { t, locale } = useI18n();
const { showErrorToast } = useToast();
const user = useUserStore();

const timeFormatter = computed(() => new Intl.DateTimeFormat(locale.value, { timeStyle: "short" }));

const groups = computed(() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekdayFormatter = new Intl.DateTimeFormat(locale.value, { weekday: "long" });
  const dateFormatter = new Intl.DateTimeFormat(locale.value, { dateStyle: "medium" });
  const result: { id: string; label: string; comments: (typeof props.comments)[number][] }[] = [];
  for (const comment of props.comments) {
    const date = comment.createdAt.toDate();
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today.getTime() - day.getTime()) / 86_400_000);
    let label = dateFormatter.format(date);
    if (diffDays === 0) label = t("comments.today");
    else if (diffDays === 1) label = t("comments.yesterday");
    else if (diffDays > 1 && diffDays < 7) label = weekdayFormatter.format(date);
    const last = result.at(-1);
    if (last?.label === label) last.comments.push(comment);
    else result.push({ id: comment.id, label, comments: [comment] });
  }
  return result;
});

const messageList = useTemplateRef<HTMLDivElement>("messageList");
watch(
  () => props.comments.length,
  async () => {
    await nextTick();
    messageList.value?.scrollTo({ top: messageList.value.scrollHeight });
  },
  { immediate: true },
);

// Opening the thread reads it, and so does every message that arrives while it is open
watch(
  () => props.unreadCount,
  async (unreadCount) => {
    if (!unreadCount) return;
    try {
      await call("markCommentsRead")({ accountId: props.accountId, targets: props.targets });
    } catch (cause) {
      const error = new Error(`Error marking comments read on account ${props.accountId}`, {
        cause,
      });
      console.error(error);
      captureException(error);
    }
  },
  { immediate: true },
);

const text = ref("");
const sending = ref(false);

const addComment = async () => {
  if (!props.targets[0] || !text.value.trim() || sending.value) return;

  sending.value = true;
  try {
    await call("addComment")({ accountId: props.accountId, ...props.targets[0], text: text.value });
    text.value = "";
  } catch (cause) {
    const error = new Error(`Error adding comment on account ${props.accountId}`, { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    sending.value = false;
  }
};
</script>
